// ═══════════════════════════════════════════════════════════════
// Handoff — All Handoff Notes
// Browse all handoffs across events with filtering
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { usePolling } from '../../hooks/usePolling';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Stack, Badge, Select, TextInput, Button,
} from '@mantine/core';
import { IconSearch, IconSun, IconMoon, IconPlus } from '@tabler/icons-react';
import { useHandoffStore } from '../../store/handoffStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { primary } from '../../lib/colors';

export default function AllHandoffs() {
  const navigate = useNavigate();
  const { canEdit } = useModulePermission('handoff');
  const { notes, loadNotes } = useHandoffStore();
  const { incidents, loadIncidents } = useAuthStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');

  useEffect(() => { loadNotes(); loadIncidents(); }, []);
  usePolling(() => { loadNotes(); loadIncidents(); });

  const eventOptions = [
    { value: 'all', label: 'All Incidents' },
    ...incidents.filter(e => e.id != null).map(e => ({ value: String(e.id), label: e.name })),
  ];

  const filtered = notes
    .filter(n => statusFilter === 'all' || n.status === statusFilter)
    .filter(n => eventFilter === 'all' || String(n.eventId) === eventFilter)
    .filter(n => !search || n.positionName.toLowerCase().includes(search.toLowerCase()) || n.senderName.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (b.datecreated ?? '').localeCompare(a.datecreated ?? ''));

  const noteStatusColor = (s: string) => s === 'draft' ? 'gray' : s === 'submitted' ? 'yellow' : 'green';

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">All Handoffs</Title>
        {canEdit && (
        <Button
          leftSection={<IconPlus size={14} />}
          size="xs"
          onClick={() => navigate('/handoff/new')}
        >
          New Handoff
        </Button>
        )}
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.currentTarget.value)} size="xs" w={200} />
        <Select data={[{ value: 'all', label: 'All Status' }, { value: 'draft', label: 'Draft' }, { value: 'submitted', label: 'Submitted' }, { value: 'acknowledged', label: 'Acknowledged' }]}
          value={statusFilter} onChange={v => setStatusFilter(v ?? 'all')} size="xs" w={160} />
        <Select data={eventOptions} value={eventFilter} onChange={v => setEventFilter(v ?? 'all')} size="xs" w={220} />
      </Group>

      {filtered.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No handoffs found.</Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {filtered.map(n => {
            const evt = incidents.find(e => String(e.id) === String(n.eventId));
            const ShiftIcon = n.fromShift === 'day' ? IconSun : IconMoon;
            return (
              <Paper key={n.id} p="sm" radius="sm" withBorder
                style={{ cursor: 'pointer', borderLeft: `3px solid ${noteStatusColor(n.status) === 'yellow' ? 'var(--color-warning)' : noteStatusColor(n.status) === 'green' ? 'var(--color-success)' : '#ccc'}` }}
                onClick={() => navigate(`/handoff/note/${n.id}`)}>
                <Group justify="space-between">
                  <div>
                    <Group gap="xs" mb={2}>
                      <ShiftIcon size={14} color={n.fromShift === 'day' ? 'var(--color-warning)' : primary[700]} />
                      <Text size="sm" fw={500}>{n.positionName}</Text>
                      <Badge size="xs" color={noteStatusColor(n.status)} tt="capitalize">{n.status}</Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {evt?.name} — {n.fromShift} → {n.toShift} — {n.shiftDate}
                      {n.senderName && ` — From: ${n.senderName}`}
                    </Text>
                  </div>
                  <Text size="xs" c="dimmed">
                    {n.datecreated ? new Date(n.datecreated).toLocaleDateString() : ''}
                  </Text>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}
    </div>
  );
}
